import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../components/shared/AuthContext";
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { ReportHeader, ReportSummaryCard } from "../../components/reports";
// amountToRelease / gst / processingCharge store PAISE.
import { toRupees } from "../../utils/money";

const fmt = (n) => (n != null ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—");
const fmtDate = (d) => { if (!d) return "—"; try { const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`; } catch { return d; } };

const COLUMNS = [
  { label: "#",                    align: "center" },
  { label: "Customer Name",        align: "left"   },
  { label: "Contact No",           align: "left"   },
  { label: "Customer ID",          align: "left"   },
  { label: "Application ID",      align: "left"   },
  { label: "Loan Account No",      align: "left"   },
  { label: "Loan Type",            align: "left"   },
  { label: "Security Deposit",     align: "right"  },
  { label: "Processing Charge",    align: "right"  },
  { label: "GST",                  align: "right"  },
  { label: "Amount To Release",    align: "right"  },
  { label: "Aadhar Number",        align: "left"   },
  { label: "Pan Number",           align: "left"   },
  { label: "Voter Id",             align: "left"   },
  { label: "Bank Account No",      align: "left"   },
  { label: "IFSC Code",            align: "left"   },
  { label: "Bank Name",            align: "left"   },
  { label: "Bank Branch",          align: "left"   },
];

const SecurityDepositReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paidFilter, setPaidFilter] = useState("all"); // all | paid | unpaid
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const branchFilter = isSuperAdmin ? {} : branchId ? { branchId } : {};
      const params = { filter: JSON.stringify(branchFilter) };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      // Fetch member_details (contains securityDeposit column) + personal_details for customer name
      const [memberRes, personalRes, bankRes, verifRes] = await Promise.all([
        axios.get("/api/getModelData/member_details", { baseURL: API_BASE_URL, params }),
        axios.get("/api/getModelData/personal_details", { baseURL: API_BASE_URL, params: { filter: JSON.stringify(branchFilter) } }),
        axios.get("/api/getModelData/bank_details",         { baseURL: API_BASE_URL }),
        axios.get("/api/getModelData/verification_details", { baseURL: API_BASE_URL }),
      ]);

      const toArr = (r) => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
      const members     = toArr(memberRes);
      const personalList = toArr(personalRes);
      const bankList    = toArr(bankRes);
      const verifList   = toArr(verifRes);

      // Build lookup maps
      const personalMap = {};
      personalList.forEach((p) => { personalMap[p.id] = p; });

      const bankMap = {};
      bankList.forEach((b) => { bankMap[b.customerId] = b; });

      const verifMap = {};
      verifList.forEach((v) => { verifMap[v.customerId] = v; });

      // Merge and filter only rows that have securityDeposit > 0
      const merged = members
        .filter((m) => Number(m.securityDeposit || 0) > 0)
        .map((m) => {
          const person = personalMap[m.customerId] || {};
          const bank   = bankMap[m.customerId]     || {};
          const verif  = verifMap[m.customerId]    || {};
          return {
            ...m,
            customer_name:     person.name             || "—",
            contact_no:        person.mobileNumber     || person.contactNo || "—",
            customer_id:       person.customerUniqueId || "—",
            address:           person.address          || "—",
            aadharNumber:      verif.aadharNo          || "—",
            panNumber:         verif.panNo             || "—",
            voterId:           verif.idProofNo         || "—",
            bankAccountNumber: bank.accountNumber      || "—",
            ifscCode:          bank.ifscCode           || "—",
            bankName:          bank.bankName           || "—",
            bankBranch:        bank.bankBranchName     || "—",
          };
        });

      setRows(merged);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [isSuperAdmin, branchId, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Apply search + paid filter
  const filtered = rows.filter((r) => {
    const matchSearch =
      (r.customer_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.ApplicationId || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.customer_id || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.contact_no || "").toLowerCase().includes(search.toLowerCase());

    const paid = Number(r.isSecurityDepositPaid || 0);
    const matchPaid =
      paidFilter === "all" ||
      (paidFilter === "paid" && paid === 1) ||
      (paidFilter === "unpaid" && paid === 0);

    return matchSearch && matchPaid;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const totalDeposit = filtered.reduce((s, r) => s + Number(r.securityDeposit || 0), 0);

  const exportCSV = () => {
    const headers = [
      "#", "Customer Name", "Contact No", "Customer ID", "Application ID",
      "Loan Account No", "Loan Type", "Security Deposit",
      "Processing Charge", "GST", "Amount To Release",
      "Aadhar Number", "Pan Number", "Voter Id",
      "Bank Account Number", "IFSC Code", "Bank Name", "Bank Branch",
    ];
    const csvRows = [
      headers.join(","),
      ...filtered.map((r, i) => [
        i + 1,
        `"${r.customer_name || ""}"`,
        r.contact_no || "",
        r.customer_id || "",
        r.ApplicationId || "",
        r.loanId || "",
        r.loanType || "",
        r.securityDeposit || 0,
        r.processingCharge || 0,
        r.gst || 0,
        // PAISE -> RUPEES for the Excel export, matching the table cell below.
        toRupees(r.amountToRelease),
        r.aadharNumber || "",
        r.panNumber || "",
        r.voterId || "",
        r.bankAccountNumber || "",
        r.ifscCode || "",
        r.bankName || "",
        r.bankBranch || "",
      ].join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "security_deposit_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <>
            {/* From Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* To Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Rows/Page */}
            <div className="relative min-w-[100px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Rows/Page
              </label>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-3 font-semibold text-xs">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="min-w-[240px] mt-1">
              <input
                placeholder="Search name / application ID / contact..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-4 font-bold"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading security deposit data..." />}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {[
          { label: "Total Security Deposit", value: `₹${fmt(totalDeposit)}`, bg: "bg-[#DDF5E6]", border: "border-[#16A34A]/25", accent: "text-[#16A34A]", icon: "fa-shield-halved" },
          { label: "Total Members", value: filtered.length, bg: "bg-[#E9DFFF]", border: "border-[#7C3AED]/25", accent: "text-[#7C3AED]", icon: "fa-users" },
        ].map((c, idx) => (
          <ReportSummaryCard key={idx} {...c} />
        ))}
      </div>

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table className="min-w-[2400px]">
          <Table.Head>
            <tr>
              {COLUMNS.map((c, idx) => (
                <Table.HeadCell key={idx} align={c.align} className="whitespace-nowrap">
                  {c.label}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : paginated.length === 0 ? (
              <Table.EmptyRow colSpan={COLUMNS.length} message="No security deposit records found." />
            ) : (
              paginated.map((r, i) => (
                <Table.Row key={i}>
                  <Table.Cell align="center" className="text-ink-400">{(currentPage - 1) * rowsPerPage + i + 1}</Table.Cell>
                  <Table.Cell className="font-semibold text-ink-900">{r.customer_name}</Table.Cell>
                  <Table.Cell>{r.contact_no}</Table.Cell>
                  <Table.Cell><code className="text-xs font-mono text-ink-600">{r.customer_id}</code></Table.Cell>
                  <Table.Cell><code className="text-xs font-mono text-primary font-bold">{r.ApplicationId || "—"}</code></Table.Cell>
                  <Table.Cell><code className="text-xs font-mono text-[#004BB5] font-bold">{r.loanId || "—"}</code></Table.Cell>
                  <Table.Cell>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-sky-50 text-sky-700 border border-sky-200">
                      {r.loanType || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell align="right" className="font-bold text-emerald-600">₹{fmt(r.securityDeposit)}</Table.Cell>
                  <Table.Cell align="right">₹{fmt(r.processingCharge)}</Table.Cell>
                  <Table.Cell align="right">₹{fmt(r.gst)}</Table.Cell>
                  {/* PAISE -> RUPEES; securityDeposit beside it was never
                      converted and stays as-is. */}
                  <Table.Cell align="right" className="font-bold">₹{fmt(toRupees(r.amountToRelease))}</Table.Cell>
                  <Table.Cell>{r.aadharNumber}</Table.Cell>
                  <Table.Cell>{r.panNumber}</Table.Cell>
                  <Table.Cell>{r.voterId}</Table.Cell>
                  <Table.Cell>{r.bankAccountNumber}</Table.Cell>
                  <Table.Cell>{r.ifscCode}</Table.Cell>
                  <Table.Cell>{r.bankName}</Table.Cell>
                  <Table.Cell>{r.bankBranch}</Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
          {filtered.length > 0 && (
            <tfoot className="border-t-2 border-border-medium bg-canvas-muted font-bold text-ink-900">
              <tr>
                <td colSpan={7} className="p-3 text-right">Total Security Deposit:</td>
                <td className="p-3 text-right text-emerald-700 font-black">₹{fmt(totalDeposit)}</td>
                <td colSpan={10} className="p-3" />
              </tr>
            </tfoot>
          )}
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records {filtered.length !== rows.length && `(filtered from ${rows.length})`}
            </span>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-ink-500 px-3 font-semibold">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default SecurityDepositReport;
